import clsx from 'clsx';
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@shared/hooks/useMediaQuery';
import { ChatPanel } from '../ChatPanel';
import { BottomNav } from '../common/BottomNav/BottomNav';
import { GlobalStockSearch } from './GlobalStockSearch';
import { SideNav, MobileMenuButton } from './SideNav';

interface AppLayoutProps {
  children: React.ReactNode;
}

const SIDEBAR_COLLAPSED_KEY = 'luminaria-sidebar-collapsed';

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // Persist collapsed state
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved === 'true';
  });

  // Mobile drawer state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Chat panel state
  const [chatOpen, setChatOpen] = useState(false);

  // Persist collapsed preference
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  // Prevent body scroll when mobile nav is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleToggleCollapse = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  const handleOpenMobile = useCallback(() => {
    setMobileOpen(true);
  }, []);

  const handleCloseMobile = useCallback(() => {
    setMobileOpen(false);
  }, []);

  const handleToggleChat = useCallback(() => {
    setChatOpen((prev) => !prev);
  }, []);

  const handleCloseChat = useCallback(() => {
    setChatOpen(false);
  }, []);

  return (
    <div className="flex min-h-screen bg-bg">
      <SideNav
        collapsed={collapsed}
        onToggleCollapse={handleToggleCollapse}
        mobileOpen={mobileOpen}
        onCloseMobile={handleCloseMobile}
      />

      <div className={clsx(
        // min-w-0: a flex item never shrinks below its content's width, so a
        // wide table would stretch the page past the viewport instead of
        // scrolling inside its own overflow-x-auto wrapper.
        'flex-1 min-w-0 flex flex-col min-h-screen transition-[margin-left] duration-300',
        !isMobile && (collapsed ? 'ml-[var(--sidebar-width-collapsed)]' : 'ml-[var(--sidebar-width)]')
      )}>
        {/* Mobile header with menu button */}
        <header className="flex md:hidden items-center gap-3 py-3 px-4 bg-bg-alt border-b border-border sticky top-0 z-50 max-[480px]:py-2 max-[480px]:px-3">
          <MobileMenuButton onClick={handleOpenMobile} />
          <span className="text-lg font-bold text-text">Luminaria</span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => navigate('/market?tab=stock')}
              className="text-text-muted hover:text-text transition-colors p-1"
              aria-label="Search stocks"
              title="Search stocks"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8" />
                <path strokeLinecap="round" d="m21 21-4.3-4.3" />
              </svg>
            </button>
            <button
              onClick={handleToggleChat}
              className="text-text-muted hover:text-text transition-colors p-1"
              aria-label="Open chat"
              title="Ask Luminaria"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </button>
          </div>
        </header>

        {/* Desktop search bar */}
        <div className="hidden md:flex items-center justify-end gap-2 px-4 py-2 sticky top-0 z-40 bg-bg border-b border-border">
          <button
            onClick={handleToggleChat}
            className="text-text-muted hover:text-text hover:bg-bg-alt transition-colors p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Open chat"
            title="Ask Luminaria"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </button>
          <GlobalStockSearch />
        </div>

        {/* Main content area */}
        <main className={clsx(
          'flex-1 p-6 max-w-[1400px] w-full mx-auto',
          'md:p-4 md:pt-0',
          'max-[480px]:px-3 max-[480px]:pt-0',
          'max-[400px]:px-2 max-[400px]:pt-0',
          isMobile && 'pb-[calc(var(--bottom-nav-height)+env(safe-area-inset-bottom))]'
        )}>
          {children}
        </main>
      </div>

      {/* Bottom navigation for mobile */}
      {isMobile && <BottomNav />}

      {/* Chat Panel */}
      <ChatPanel isOpen={chatOpen} onClose={handleCloseChat} />
    </div>
  );
};

export default AppLayout;
